import { SeeqNames } from '@/main/app.constants.seeqnames';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { PropertyMatchOperatorEnum } from '@/sdk/model/ColumnRuleDescendantInputV1';
import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import type { ColumnRuleAncestorInputV1 } from '@/sdk/model/ColumnRuleAncestorInputV1';
import type { ColumnRuleAssetCreatorInputV1 } from '@/sdk/model/ColumnRuleAssetCreatorInputV1';
import type { ColumnRuleConcatInputV1 } from '@/sdk/model/ColumnRuleConcatInputV1';
import type { ColumnRuleConstantInputV1 } from '@/sdk/model/ColumnRuleConstantInputV1';
import type { ColumnRuleContextConditionInputV1 } from '@/sdk/model/ColumnRuleContextConditionInputV1';
import type { ColumnRuleDescendantInputV1 } from '@/sdk/model/ColumnRuleDescendantInputV1';
import type { ColumnRuleDescendantSearchInputV1 } from '@/sdk/model/ColumnRuleDescendantSearchInputV1';
import type { ColumnRuleEventAssetCreatorInputV1 } from '@/sdk/model/ColumnRuleEventAssetCreatorInputV1';
import type { ColumnRuleEventPropertyInputV1 } from '@/sdk/model/ColumnRuleEventPropertyInputV1';
import type { ColumnRuleFindDescendantInputV1 } from '@/sdk/model/ColumnRuleFindDescendantInputV1';
import type { ColumnRuleFindPropertyInputV1 } from '@/sdk/model/ColumnRuleFindPropertyInputV1';
import type { ColumnRuleFormulaCreatorInputV1 } from '@/sdk/model/ColumnRuleFormulaCreatorInputV1';
import type { ColumnRuleGetItemPropertyInputV1 } from '@/sdk/model/ColumnRuleGetItemPropertyInputV1';
import type { ColumnRuleInputV1 } from '@/sdk/model/ColumnRuleInputV1';
import type { ColumnRuleItemSearchInputV1 } from '@/sdk/model/ColumnRuleItemSearchInputV1';
import type { ColumnRuleManagerInputV1 } from '@/sdk/model/ColumnRuleManagerInputV1';
import type { ColumnRulePathInputV1 } from '@/sdk/model/ColumnRulePathInputV1';
import type { ColumnRulePathSearchInputV1 } from '@/sdk/model/ColumnRulePathSearchInputV1';
import type { ColumnRuleScalarCreatorInputV1 } from '@/sdk/model/ColumnRuleScalarCreatorInputV1';
import type { ColumnRuleSearchAncestorInputV1 } from '@/sdk/model/ColumnRuleSearchAncestorInputV1';
import type { ColumnRuleSetItemPropertyInputV1 } from '@/sdk/model/ColumnRuleSetItemPropertyInputV1';
import type { ColumnRuleTextExtractorInputV1 } from '@/sdk/model/ColumnRuleTextExtractorInputV1';
import type { ColumnRuleTextReplacementInputV1 } from '@/sdk/model/ColumnRuleTextReplacementInputV1';
import type { ColumnRuleTreePathCreatorInputV1 } from '@/sdk/model/ColumnRuleTreePathCreatorInputV1';
import type {
  ColumnRuleWithMetadata,
  ParameterWithMetadata,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import type {
  ColumnDefinition,
  DecoratedColumnDefinition,
  EditorType,
  PotentialColumnDefinition,
} from '@/tableDefinitionEditor/tableDefinition.types';

export type ColumnRuleInputParameters =
  | ColumnRuleGetItemPropertyInputV1
  | ColumnRulePathInputV1
  | ColumnRuleAncestorInputV1
  | ColumnRuleConcatInputV1
  | ColumnRuleDescendantInputV1
  | ColumnRuleEventPropertyInputV1
  | ColumnRuleFormulaCreatorInputV1
  | ColumnRuleAssetCreatorInputV1
  | ColumnRuleTreePathCreatorInputV1
  | ColumnRuleConstantInputV1
  | ColumnRuleSetItemPropertyInputV1
  | ColumnRulePathSearchInputV1
  | ColumnRuleTextExtractorInputV1
  | ColumnRuleTextReplacementInputV1
  | ColumnRuleScalarCreatorInputV1
  | ColumnRuleManagerInputV1
  | ColumnRuleContextConditionInputV1
  | ColumnRuleSearchAncestorInputV1
  | ColumnRuleEventAssetCreatorInputV1
  | ColumnRuleDescendantSearchInputV1
  | ColumnRuleFindDescendantInputV1;

export type CombinedColumnRuleInputParameters = ColumnRuleGetItemPropertyInputV1 &
  ColumnRulePathInputV1 &
  ColumnRuleAncestorInputV1 &
  ColumnRuleConcatInputV1 &
  ColumnRuleDescendantInputV1 &
  ColumnRuleEventPropertyInputV1 &
  ColumnRuleFormulaCreatorInputV1 &
  ColumnRuleAssetCreatorInputV1 &
  ColumnRuleTreePathCreatorInputV1 &
  ColumnRuleConstantInputV1 &
  ColumnRuleSetItemPropertyInputV1 &
  ColumnRulePathSearchInputV1 &
  ColumnRuleTextExtractorInputV1 &
  ColumnRuleTextReplacementInputV1 &
  ColumnRuleItemSearchInputV1 &
  ColumnRuleScalarCreatorInputV1 &
  ColumnRuleManagerInputV1 &
  ColumnRuleContextConditionInputV1 &
  ColumnRuleSearchAncestorInputV1 & { fullPath: boolean } & ColumnRuleEventAssetCreatorInputV1 &
  ColumnRuleDescendantSearchInputV1 &
  ColumnRuleFindPropertyInputV1 &
  ColumnRuleFindDescendantInputV1;

export interface ColumnRuleInput {
  ancestor?: ColumnRuleAncestorInputV1;
  assetCreator?: ColumnRuleAssetCreatorInputV1;
  concatColumns?: ColumnRuleConcatInputV1;
  constant?: ColumnRuleConstantInputV1;
  descendant?: ColumnRuleDescendantInputV1;
  descendantSearch?: ColumnRuleDescendantSearchInputV1;
  eventProperty?: ColumnRuleEventPropertyInputV1;
  formulaCreator?: ColumnRuleFormulaCreatorInputV1;
  getItemProperty?: ColumnRuleGetItemPropertyInputV1;
  itemSearch?: ColumnRuleItemSearchInputV1;
  path?: ColumnRulePathInputV1;
  pathSearch?: ColumnRulePathSearchInputV1;
  scalarCreator?: ColumnRuleScalarCreatorInputV1;
  numericScalarCreator?: ColumnRuleScalarCreatorInputV1;
  stringScalarCreator?: ColumnRuleScalarCreatorInputV1;
  setItemProperty?: ColumnRuleSetItemPropertyInputV1;
  textExtractor?: ColumnRuleTextExtractorInputV1;
  textReplacement?: ColumnRuleTextReplacementInputV1;
  treePathCreator?: ColumnRuleTreePathCreatorInputV1;
  AssetTreeCreator?: ColumnRuleTreePathCreatorInputV1;
  AssetTreeAddChild?: ColumnRuleTreePathCreatorInputV1;
  contextCondition?: ColumnRuleContextConditionInputV1;
  searchAncestor?: ColumnRuleSearchAncestorInputV1;
  eventAssetCreator?: ColumnRuleEventAssetCreatorInputV1;
  findProperty?: ColumnRuleFindPropertyInputV1;
  findDescendant?: ColumnRuleFindDescendantInputV1;
  itemFindDescendant?: ColumnRuleFindDescendantInputV1;
  stringFindDescendant?: ColumnRuleFindDescendantInputV1;
}

export type ColumnRule = keyof ColumnRuleInput;
export type RuleParameter = keyof CombinedColumnRuleInputParameters;

// TODO CRAB-41437 unrestrict these permissions options
export const restrictedParams: string[] = [
  SeeqNames.MaterializedTables.Parameters.IdentityId,
  SeeqNames.MaterializedTables.Parameters.PermissionString,
];

export const ItemTypeOptions = [
  { value: SeeqNames.Types.Condition, label: 'ITEM_FINDER.TYPES_CONDITION' },
  { value: SeeqNames.Types.Signal, label: 'ITEM_FINDER.TYPES_SIGNAL' },
  { value: SeeqNames.Types.Scalar, label: 'ITEM_FINDER.TYPES_SCALAR' },
  { value: SeeqNames.Types.Asset, label: 'ITEM_FINDER.TYPES_ASSET' },
];

export const PropertyNameOptions = [
  { value: SeeqNames.Properties.Name, label: 'ITEM_FINDER.PROPERTY_SELECT_NAME' },
  { value: SeeqNames.Properties.Description, label: 'ITEM_FINDER.PROPERTY_SELECT_DESCRIPTION' },
  { value: SeeqNames.Properties.DatasourceId, label: 'ITEM_FINDER.PROPERTY_SELECT_DATASOURCE_ID' },
  { value: SeeqNames.Properties.DatasourceClass, label: 'ITEM_FINDER.PROPERTY_SELECT_DATASOURCE_CLASS' },
];

export const PropertyMatchOperatorEnumToLabel: Record<PropertyMatchOperatorEnum, string> = {
  [PropertyMatchOperatorEnum.EQUALS]: 'EQUALS',
  [PropertyMatchOperatorEnum.EQUALSIGNORECASE]: 'EQUALS_IGNORE_CASE',
  [PropertyMatchOperatorEnum.NOTEQUALS]: 'NOT_EQUALS',
  [PropertyMatchOperatorEnum.WITHIN]: 'SCALING.PROPERTY_OPERATORS.WITHIN',
  [PropertyMatchOperatorEnum.STRINGCONTAINS]: 'SCALING.PROPERTY_OPERATORS.STRING_CONTAINS',
  [PropertyMatchOperatorEnum.NOTSTRINGCONTAINS]: 'SCALING.PROPERTY_OPERATORS.NOT_STRING_CONTAINS',
  [PropertyMatchOperatorEnum.REGEXMATCHES]: 'SCALING.PROPERTY_OPERATORS.REGEX_MATCHES',
  [PropertyMatchOperatorEnum.NOTREGEXMATCHES]: 'SCALING.PROPERTY_OPERATORS.NOT_REGEX_MATCHES',
};

export const ColumnTypeOptions = [
  {
    value: ColumnTypeEnum.TEXT,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.TEXT.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.TEXT.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.NUMERIC,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.NUMERIC.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.NUMERIC.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.UUID,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.UUID.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.UUID.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.TIMESTAMPTZ,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.TIMESTAMPTZ.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.TIMESTAMPTZ.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.BOOLEAN,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.BOOLEAN.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.BOOLEAN.DESCRIPTION',
  },
] as const;

export const AllColumnEnumOptions = Object.values(ColumnTypeEnum) as ColumnTypeEnum[];

export interface BaseInputParametersProps {
  setParameter: (parameter: RuleParameter, value: any) => void;
  testId: string;
  ruleParameterWithMetadata: ParameterWithMetadata;
  currentValue?: any;
  extraClassNames?: string;
  ruleType: ColumnRuleWithMetadata;
  columnType?: ColumnTypeEnum;
  showError: boolean;
  setShowError: (showError: boolean) => void;
  isBeingEdited?: boolean;
  currentRuleState: Partial<CombinedColumnRuleInputParameters>;
  skipDefaultValue?: boolean;
  skipLabel?: boolean;
  disabled?: boolean;
  extraValue?: any;
}

export interface OptionDropdownInputProps extends BaseInputParametersProps {
  dropdownOptionsOverride?: { value: string; label: string }[];
}

export interface TextInputParameterProps extends BaseInputParametersProps {
  placeholderOverride?: string;
  readonly?: boolean;
  extraClassNames?: string;
  isBeingEdited?: boolean;
  disabled?: boolean;
}

export interface BooleanInputProps extends BaseInputParametersProps {}

export interface ColumnDropdownInputProps extends BaseInputParametersProps {
  allColumnDefinitions: DecoratedColumnDefinition[];
  currentColumnDefinition?: ColumnDefinitionInputV1;
  potentialColumnsNotYetCreated?: PotentialColumnDefinition[];
  nonStandardColumns?: ColumnDefinition[];
  firstIsCreatable?: boolean;
  onRenameFirstColumn?: (newName: string) => void;
  firstColumnIsRestricted?: boolean;
  otherColumnsAreRestricted?: boolean;
  defaultDropdownDescription?: string;
  hidePotentialColumns?: boolean;
  showHiddenInputsOverride?: boolean;
  renderHiddenColumnToggle?: boolean;
  isLabelBold?: boolean;
  potentialTextColumnPlacement?: 'top' | 'bottom';
  editorType: EditorType;
}

export interface ConfigureTreeCreationOrUpdatePseudoRulesProps {
  allColumnDefinitions: ColumnDefinition[];
  currentRuleState: Partial<CombinedColumnRuleInputParameters>;
  setParameter: (parameter: RuleParameter, value: any, skipUpdateColumnDefToCreate: boolean) => void;
  currentColumnDefinition?: ColumnDefinitionInputV1;
  columnRuleToEdit?: ColumnRuleTreePathCreatorInputV1;
  columnType?: ColumnTypeEnum;
  getPotentialColumns: (
    columnDefinitionsToCreate: PotentialColumnDefinition[],
    potentialRootColumnPairIfNotAlreadySelectedForCreation?: {
      rootText: PotentialColumnDefinition;
      rootAsset: PotentialColumnDefinition;
    },
  ) => PotentialColumnDefinition[];
  setPotentialAdditionalColumns: React.Dispatch<React.SetStateAction<PotentialColumnDefinition[]>>;
  potentialAdditionalColumns: PotentialColumnDefinition[];
  fetchDecoratedColumns: (
    fetchAllowedColumnsForFirstInput: (
      columns: DecoratedColumnDefinition[],
    ) => { columnName: string; description: string }[],
  ) => Promise<DecoratedColumnDefinition[]>;
  setDecoratedColumnDefinitions: React.Dispatch<React.SetStateAction<DecoratedColumnDefinition[]>>;
  decoratedColumnDefinitions: DecoratedColumnDefinition[];
  setColumnDefinitionsToCreate: React.Dispatch<React.SetStateAction<PotentialColumnDefinition[]>>;
  columnDefinitionsToCreate: PotentialColumnDefinition[];
  showError: boolean;
  setShowError: (showError: boolean) => void;
  editorType: EditorType;
}

export interface CombinedInputProps
  extends TextInputParameterProps, ColumnDropdownInputProps, BooleanInputProps, OptionDropdownInputProps {}

export interface ColumnRuleInputWithUIMetadata extends ColumnRuleInputV1 {
  isCollapsed?: boolean;
}

export const CalculateOrDefineValuesRuleCategory = 'SCALING.RULE_CATEGORIES.CALCULATE_OR_DEFINE_VALUES';
export const FindDataRuleCategory = 'SCALING.RULE_CATEGORIES.FIND_DATA';
export const FindOrChangeTextRuleCategory = 'SCALING.RULE_CATEGORIES.FIND_OR_CHANGE_TEXT';
export const StructureDataRuleCategory = 'SCALING.RULE_CATEGORIES.STRUCTURE_DATA';

export const CanonicalCategoryOrder = [
  CalculateOrDefineValuesRuleCategory,
  FindDataRuleCategory,
  FindOrChangeTextRuleCategory,
  StructureDataRuleCategory,
];

export const CanonicalCalculateRuleOrder: string[] = [
  SeeqNames.MaterializedTables.Rules.FormulaCreator,
  SeeqNames.MaterializedTables.Rules.Constant.BaseConstant,
  SeeqNames.MaterializedTables.Rules.ScalarCreator.BaseScalarCreator,
  SeeqNames.MaterializedTables.Rules.SetItemProperty,
];

export const CanonicalFindDataRuleOrder: string[] = [
  SeeqNames.MaterializedTables.Rules.ItemSearch,
  SeeqNames.MaterializedTables.Rules.GetItemProperty,
  SeeqNames.MaterializedTables.Rules.EventProperty,
  SeeqNames.MaterializedTables.Rules.FindDescendant.BaseFindDescendant,
  SeeqNames.MaterializedTables.Rules.DescendantSearch,
  SeeqNames.MaterializedTables.Rules.Descendant,
  SeeqNames.MaterializedTables.Rules.Ancestor,
  SeeqNames.MaterializedTables.Rules.SearchAncestor,
  SeeqNames.MaterializedTables.Rules.FindProperty,
  SeeqNames.MaterializedTables.Rules.Path,
  SeeqNames.MaterializedTables.Rules.PathSearch,
  SeeqNames.MaterializedTables.Rules.ContextCondition,
];

export const CanonicalFindOrChangeTextRuleOrder: string[] = [
  SeeqNames.MaterializedTables.Rules.ConcatColumns,
  SeeqNames.MaterializedTables.Rules.TextExtractor,
  SeeqNames.MaterializedTables.Rules.TextReplacement,
];

export const CanonicalStructureDataRuleOrder: string[] = [
  SeeqNames.MaterializedTables.Rules.TreePathCreator,
  SeeqNames.MaterializedTables.Rules.AssetCreator,
  SeeqNames.MaterializedTables.Rules.EventAssetCreator,
];

export const RuleGroupToRuleOrder: Record<string, string[]> = {
  [CanonicalCategoryOrder[0]]: CanonicalCalculateRuleOrder,
  [CanonicalCategoryOrder[1]]: CanonicalFindDataRuleOrder,
  [CanonicalCategoryOrder[2]]: CanonicalFindOrChangeTextRuleOrder,
  [CanonicalCategoryOrder[3]]: CanonicalStructureDataRuleOrder,
};
